/**
 * AdminAssignRolesPage Component
 *
 * Admin page for assigning gym roles (owner, setter, climber) to users.
 * Allows admins to manage gym memberships directly.
 */

import { useState, useEffect, type FormEvent, type CSSProperties } from 'react';
import { collection, doc, setDoc, getDocs, serverTimestamp } from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import { getGymMembershipId } from '@flash/shared';
import type { User, Gym, GymRole } from '@flash/shared';
import { ToggleButtonGroup, type ToggleButtonOption } from '@/components/ui/ToggleButtonGroup';

/**
 * Role options for the toggle button group
 */
const roleOptions: ToggleButtonOption<GymRole>[] = [
  { value: 'owner', label: 'Owner', variant: 'warning' },
  { value: 'setter', label: 'Setter', variant: 'info' },
  { value: 'climber', label: 'Climber', variant: 'success' },
];

/**
 * AdminAssignRolesPage allows admins to assign gym roles
 */
export const AdminAssignRolesPage = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [gyms, setGyms] = useState<Gym[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [userId, setUserId] = useState('');
  const [gymId, setGymId] = useState('');
  const [role, setRole] = useState<GymRole>('climber');

  // Fetch users and gyms
  useEffect(() => {
    const fetchData = async () => {
      try {
        const db = getFirebaseDb();

        // Fetch users
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const usersData = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as User[];
        setUsers(usersData);

        // Fetch gyms
        const gymsSnapshot = await getDocs(collection(db, 'gyms'));
        const gymsData = gymsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Gym[];
        setGyms(gymsData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!userId) {
      setError('Please select a user');
      return;
    }
    if (!gymId) {
      setError('Please select a gym');
      return;
    }

    setSubmitting(true);
    try {
      const db = getFirebaseDb();
      const membershipId = getGymMembershipId(userId, gymId);

      const membershipData = {
        odKey: membershipId,
        userId,
        gymId,
        role,
        isHomeGym: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(doc(db, 'gymMemberships', membershipId), membershipData, { merge: true });

      const user = users.find((u) => u.id === userId);
      const gym = gyms.find((g) => g.id === gymId);
      setSuccess(`Assigned ${role} role to ${user?.displayName ?? user?.email} at ${gym?.name}`);

      // Reset form
      setUserId('');
      setGymId('');
      setRole('climber');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign role');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main style={containerStyle}>
        <div style={loadingContainerStyle}>
          <p style={loadingTextStyle}>Loading...</p>
        </div>
      </main>
    );
  }

  return (
    <main style={containerStyle}>
      <div style={contentStyle}>
        {/* Header */}
        <header style={headerStyle}>
          <h1 style={titleStyle}>Assign Gym Roles</h1>
          <p style={subtitleStyle}>Assign owner, setter, or climber roles to users at gyms</p>
        </header>

        {/* Form */}
        <div style={formContainerStyle}>
          <form onSubmit={handleSubmit}>
            {error && <div style={errorMessageStyle}>{error}</div>}
            {success && <div style={successMessageStyle}>{success}</div>}

            <div style={formGridStyle}>
              {/* User */}
              <div style={formGroupStyle}>
                <label htmlFor="user" style={labelStyle}>
                  User *
                </label>
                <select
                  id="user"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  disabled={submitting}
                  style={selectStyle}
                >
                  <option value="">Select a user...</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.displayName ?? user.email} ({user.email})
                    </option>
                  ))}
                </select>
              </div>

              {/* Gym */}
              <div style={formGroupStyle}>
                <label htmlFor="gym" style={labelStyle}>
                  Gym *
                </label>
                <select
                  id="gym"
                  value={gymId}
                  onChange={(e) => setGymId(e.target.value)}
                  disabled={submitting}
                  style={selectStyle}
                >
                  <option value="">Select a gym...</option>
                  {gyms.map((gym) => (
                    <option key={gym.id} value={gym.id}>
                      {gym.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Role */}
              <div style={formGroupFullStyle}>
                <label htmlFor="role" style={labelStyle}>
                  Role *
                </label>
                <ToggleButtonGroup
                  options={roleOptions}
                  value={role}
                  onChange={setRole}
                  disabled={submitting}
                  ariaLabel="Gym role"
                />
                <p style={roleDescriptionStyle}>
                  {role === 'owner' &&
                    'Full access to gym settings, can invite setters and manage gym'}
                  {role === 'setter' && 'Can create and edit routes at this gym'}
                  {role === 'climber' && 'Can log climbs and submit grades/ratings'}
                </p>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              style={{
                ...submitButtonStyle,
                ...(submitting ? disabledButtonStyle : {}),
              }}
            >
              {submitting ? 'Assigning...' : 'Assign Role'}
            </button>
          </form>
        </div>

        {/* Info Box */}
        <div style={infoBoxStyle}>
          <h3 style={infoTitleStyle}>Role Hierarchy</h3>
          <ul style={infoListStyle}>
            <li>
              <strong>Owner</strong> - Full control of gym settings, can invite setters and other
              owners
            </li>
            <li>
              <strong>Setter</strong> - Can create, edit, and retire routes at the gym
            </li>
            <li>
              <strong>Climber</strong> - Can log climbs, submit grade suggestions, and rate routes
            </li>
          </ul>
        </div>
      </div>
    </main>
  );
};

/**
 * Styles
 */
const containerStyle: CSSProperties = {
  minHeight: '100%',
  backgroundColor: 'var(--color-bg-secondary)',
};

const contentStyle: CSSProperties = {
  maxWidth: '600px',
  margin: '0 auto',
  padding: '20px',
};

const loadingContainerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100%',
};

const loadingTextStyle: CSSProperties = {
  fontSize: '1.125rem',
  color: 'var(--color-text-secondary)',
};

const headerStyle: CSSProperties = {
  marginBottom: '24px',
};

const titleStyle: CSSProperties = {
  fontSize: '1.75rem',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  margin: '12px 0 4px 0',
};

const subtitleStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'var(--color-text-secondary)',
  margin: 0,
};

const formContainerStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '12px',
  border: '1px solid var(--color-border)',
  padding: '24px',
  marginBottom: '20px',
};

const formGridStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  marginBottom: '24px',
};

const formGroupStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
};

const formGroupFullStyle: CSSProperties = {
  ...formGroupStyle,
};

const labelStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-primary)',
};

const selectStyle: CSSProperties = {
  padding: '10px 14px',
  fontSize: '1rem',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  backgroundColor: 'var(--color-bg-primary)',
  boxSizing: 'border-box',
  cursor: 'pointer',
};

const roleDescriptionStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-secondary)',
  marginTop: '8px',
  fontStyle: 'italic',
};

const submitButtonStyle: CSSProperties = {
  width: '100%',
  padding: '12px 24px',
  backgroundColor: '#3b82f6',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 600,
  cursor: 'pointer',
};

const disabledButtonStyle: CSSProperties = {
  opacity: 0.7,
  cursor: 'not-allowed',
};

const errorMessageStyle: CSSProperties = {
  padding: '12px',
  borderRadius: '8px',
  backgroundColor: '#fef2f2',
  border: '1px solid #fecaca',
  color: '#991b1b',
  fontSize: '0.875rem',
  marginBottom: '16px',
};

const successMessageStyle: CSSProperties = {
  padding: '12px',
  borderRadius: '8px',
  backgroundColor: '#f0fdf4',
  border: '1px solid #bbf7d0',
  color: '#166534',
  fontSize: '0.875rem',
  marginBottom: '16px',
};

const infoBoxStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: '12px',
  border: '1px solid var(--color-border)',
  padding: '20px',
};

const infoTitleStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  marginBottom: '12px',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const infoListStyle: CSSProperties = {
  margin: 0,
  paddingLeft: '20px',
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.8,
};

export default AdminAssignRolesPage;
